import React, { useState } from "react";
import "./App.css";

import SETTINGS from "./globals";
import AppSections from "./utils/AppSections";

import { ApiClient } from "./shared/ApiClient";
import { ApiClientProvider } from "./contexts/ApiClientContext";
import { AuthManager } from "./shared/AuthManager";
import { AuthProvider } from "./contexts/AuthContext";
import { AlertsProvider } from "./contexts/AlertsContext";

import { Route, Routes, useNavigate } from "react-router-dom";
import ProtectedRoute from "./routes/ProtectedRoute";
import Authenticated from "./ui/Authenticated";
import SignInSSO from "./ui/signin/SignInSSO";
import OAuthCallback from "./ui/signin/OAuthCallback";
import SignUp from "./ui/signin/SignUp";
import MyProfile from "./ui/my_profile/MyProfile";
import SitesRoutes from "./routes/SitesRoutes";
import AlertMessage from "./ui/common/alert_message/AlertMessage";
import PilotsRoutes from "./routes/PilotsRoutes";
import IDialog from "./ui/common/dialog_window/IDialog";
import DialogWindow from "./ui/common/dialog_window/DialogWindow";
import IAlert from "./ui/common/alert_message/IAlert";
import AlertMessageType from "./ui/common/alert_message/AlertMessageType";
import ChElementsRoutes from "./routes/ChElementsRoutes";
import ClientsSSORoutes from "./routes/ClientsSSORoutes";
import E3DsRoutes from "./routes/E3dRoutes";
import _3dInstanceRoutes from "./routes/_3dInstancesRoutes";
import SpatialRelationsRoutes from "./routes/SpatialRelationsRoutes";
import TexturesRoutes from "./routes/TexturesRoutes";
import AnnexDataRoutes from "./routes/AnnexDataRoutes";
import DashboardMap from "./ui/dashboard/DashboardMap";
import AlterationFormRoutes from "./routes/AlterationFormRoutes";
import MaterialRoutes from "./routes/MaterialRoutes";
import UserRoutes from "./routes/UserRoutes";
import Search from "./ui/search/Search";
import AnnotationsRoutes from "./routes/AnnotationsRoutes";
import ConditionReportsRoutes from "./routes/ConditionReportsRoutes";
import ToolSSO from "./ui/toolsso/ToolSSO";
import CollectionsRoutes from "./routes/CollectionsRoutes";
import MyListsRoutes from "./routes/MyListsRoutes";
import IGroupExplore from "./ui/common/group_explore/IGroupExplore";
import GroupExplore from "./ui/common/group_explore/GroupExplore";
import TermsAndConditions from "./ui/static_pages_display/TermsAndConditions";
import CommunityRules from "./ui/static_pages_display/CommunityRules";
import StaticPagesRoutes from "./routes/StaticPagesRoutes";
import EmailConfirmation from "./ui/signin/EmailConfirmation";
import Welcome from "./ui/Welcome";
import LicenseAgreement from "./ui/static_pages_display/LicenseAgreement";

// use this component to manage the authentication token
const authManager = new AuthManager();
// cannot instantiate ApiClient inside App component
// because it would generate unnecessary re-renders
const apiClient = new ApiClient(SETTINGS.API_ROOT, authManager);

function App() {
  const [alertMessageInfo, setAlertMessageInfo] = useState<IAlert | undefined>(
    undefined,
  );

  const [dialogWindowInfo, setDialogWindowInfo] = useState<IDialog | undefined>(
    undefined,
  );

  const [groupExploreInfo, setGroupExploreInfo] = useState<
    IGroupExplore | undefined
  >(undefined);

  const navigate = useNavigate();

  const userAuthenticated = (token: string, redirect = true) => {
    if (!authManager.setToken(token)) {
      setAlertMessageInfo({
        type: AlertMessageType.ERROR,
        message: "Authentication token is invalid.",
      });
    } else if (redirect) {
      navigate(SETTINGS.get_path({ section: AppSections.CHELEMENTS }));
    }
  };

  const userNotAuthenticated = () => {
    authManager.setToken("");
    navigate(SETTINGS.get_path({ section: AppSections.SIGN_IN }));
  };

  const onNetworkError = () => {
    setAlertMessageInfo({
      type: AlertMessageType.ERROR,
      message:
        "Cannot reach the server. Please check your internet connection.",
    });
  };

  const onAccessDenied = () => {
    setAlertMessageInfo({
      type: AlertMessageType.ERROR,
      message: "You are not allowed to perform this action.",
    });
  };

  const onServerError = () => {
    setAlertMessageInfo({
      type: AlertMessageType.ERROR,
      message: "Server error. Please try again later.",
    });
  };

  const closeDialogWindow = () => {
    setDialogWindowInfo(undefined);
  };

  const closeAlertMessage = () => {
    setAlertMessageInfo(undefined);
  };

  const closeGroupExplore = () => {
    setGroupExploreInfo(undefined);
  };

  // configure the callbacks for the API Client
  apiClient.onSessionEnd = userNotAuthenticated;
  apiClient.onNetworkError = onNetworkError;
  apiClient.onServerError = onServerError;
  apiClient.onAccessDenied = onAccessDenied;

  console.log("dialogWindowInfo", dialogWindowInfo);

  return (
    <div className="App">
      {alertMessageInfo && (
        <AlertMessage
          {...alertMessageInfo}
          onCloseDefault={closeAlertMessage}
        />
      )}

      {groupExploreInfo && (
        <GroupExplore
          {...groupExploreInfo}
          onCloseDefault={closeGroupExplore}
        />
      )}

      <AuthProvider value={authManager}>
        <ApiClientProvider value={apiClient}>
          <AlertsProvider
            value={{
              showAlertMessage: setAlertMessageInfo,
              showDialogWindow: setDialogWindowInfo,
              showGroupExplore: setGroupExploreInfo,
            }}
          >
            <>
              {dialogWindowInfo && (
                <DialogWindow
                  {...dialogWindowInfo}
                  onCloseDefault={closeDialogWindow}
                />
              )}
              <Routes>
                <Route path="/" element={<Welcome />} index />
                <Route
                  path="/sign-in"
                  //element={<SignIn onAuthenticated={userAuthenticated} />}
                  element={<SignInSSO onAuthenticated={userAuthenticated} />}
                  index
                />
                <Route path="/signup" element={<SignUp />} index />
                <Route
                  path="/auth/callback"
                  element={
                    <OAuthCallback onAuthenticated={userAuthenticated} />
                  }
                />
                <Route path="/toolsso" element={<ToolSSO />} index />
                <Route
                  path="/terms-and-conditions"
                  element={<TermsAndConditions />}
                  index
                />
                <Route
                  path="/email-confirmation/:actionStatus"
                  element={<EmailConfirmation />}
                  index
                />
                <Route
                  path="/community-rules"
                  element={<CommunityRules />}
                  index
                />
                <Route
                  path="/license-agreement"
                  element={<LicenseAgreement />}
                  index
                />
                <Route
                  element={
                    <ProtectedRoute>
                      <Authenticated />
                    </ProtectedRoute>
                  }
                >
                  <Route
                    path={AppSections.MY_PROFILE}
                    element={<MyProfile />}
                  />
                  <Route
                    path={AppSections.DASHBOARD}
                    element={<DashboardMap />}
                  />
                  <Route path={AppSections.SEARCH} element={<Search />} />
                  {SitesRoutes}
                  {PilotsRoutes}
                  {ConditionReportsRoutes}
                  {ChElementsRoutes}
                  {ClientsSSORoutes}
                  {E3DsRoutes}
                  {_3dInstanceRoutes}
                  {TexturesRoutes}
                  {SpatialRelationsRoutes}
                  {AnnexDataRoutes}
                  {AlterationFormRoutes}
                  {MaterialRoutes}
                  {StaticPagesRoutes}
                  {UserRoutes}
                  {AnnotationsRoutes}
                  {CollectionsRoutes}
                  {MyListsRoutes}
                </Route>
              </Routes>
            </>
          </AlertsProvider>
        </ApiClientProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
